import { Components } from "appworks/components/components";
import { gameState } from "appworks/model/game-state";
import { Model } from "appworks/model/model";
import { commsManager } from "appworks/server/comms-manager";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { JurisdictionComponent } from "slotworks/components/jurisdiction/jurisdiction-component";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { ReelSpinner } from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { SpinRequestPayload } from "slotworks/model/gameplay/requests/spin-request-payload";
import { AutoplayService } from "slotworks/services/autoplay/autoplay-service";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";
import { JurisdictionService } from "slotworks/services/jurisdiction/jurisdiction-service";

export interface RequestSpinConfig {
    winningsResetDelay: number;
    /** Change this if using this state with a secondary matrix component */
    matrixComponentType: { new (...args: any[]): AbstractMatrixComponent };
}

export class RequestSpinState extends State {
    protected config: RequestSpinConfig = {
        winningsResetDelay: 0,
        matrixComponentType: AbstractMatrixComponent
    };

    protected skipping: boolean;

    constructor(config?: Partial<RequestSpinConfig>) {
        super();

        if (config) {
            this.config = { ...this.config, ...config };
        }
    }

    public onEnter(cascadeSkip?: boolean) {
        this.skipping = cascadeSkip;

        if (this.skipping || !Services.get(JurisdictionService).getSpinSkippingEnabled()) {
            uiFlags.set(UIFlag.NO_SKIP, true);
        }

        this.newGame();

        ReelSpinner.disableMinSpinTime = false;

        uiFlags.set(UIFlag.IDLE, false);
        uiFlags.set(UIFlag.GAME_IN_PROGRESS, true);
        uiFlags.set(UIFlag.AWAITING_RESPONSE, true);
        uiFlags.closeMenus();

        Components.get(FooterComponent)?.showSpinStart(Services.get(SlotBetService).getTotalStake());
        Components.get(JurisdictionComponent)?.startSpin();

        this.startSpinning().then(() => {
            if (!gameState.getCurrentGame().getCurrentRecord()) {
                this.error();
            } else {
                if (this.skipping) {
                    this.skip();
                } else {
                    this.complete();
                }
                if (gameState.getCurrentGame().getCurrentRecord() instanceof SpinRecord && !uiFlags.has(UIFlag.FREE_BETS)) {
                    Services.get(JurisdictionService).betStaked(Services.get(SlotBetService).getTotalStake());
                }
            }
        });
    }

    public error() {
        Components.get(this.config.matrixComponentType).reset();
        Services.get(AutoplayService).stop();
        Model.write({ settings: { turboSpin: false } });

        uiFlags.set(UIFlag.GAME_IN_PROGRESS, false);
        uiFlags.set(UIFlag.NO_SKIP, false);

        Services.get(TransactionService).setBalance(gameState.getCurrentGame().startBalance);

        super.error();
    }

    public onSkip() {
        uiFlags.set(UIFlag.NO_SKIP, true);
        this.skipping = true;
    }

    public onExit() {
        uiFlags.set(UIFlag.AWAITING_RESPONSE, false);
    }

    protected newGame() {
        gameState.newGame(Services.get(SlotBetService).getTotalStake());

        Services.get(TransactionService).setDelayedWinnings(0, 0, this.config.winningsResetDelay).execute();
    }

    protected startSpinning(): Contract<any> {
        return new Parallel([
            () => this.startReels(),
            () => this.requestSpin()
        ]);
    }

    protected startReels() {
        const gameplay = gameState.getCurrentGame();

        Services.get(TransactionService).setBalance(gameplay.startBalance);
    
        const record = gameplay.getCurrentRecord();
        if (record && (record instanceof FreespinRecord || record.getFreespinsWon() > 0)) {
            Services.get(SoundService).event(SoundEvent.start_freespin);
        } else {
            Services.get(SoundService).event(SoundEvent.start_spin);
        }
    
        const settings = Model.read().settings;
        const quickSpin = settings.quickSpin || settings.turboSpin;
    
        return Components.get(AbstractMatrixComponent).startTransition(false, quickSpin);
    }

    protected requestSpin() {
        const payload = new SpinRequestPayload();
        payload.creditPerLine = Services.get(SlotBetService).getCreditPerLine();
        payload.creditSize = Services.get(SlotBetService).getCreditSize();
    
        return commsManager.request(payload);
    }
}
